import { type FilterDefinition } from "@/shared/components/ResourceView";

/**
 * The native (non-custom-field) paths a chart card can be grouped by.
 *
 * One entry, and structurally so rather than out of caution. A group key must
 * be a single SELECT-able expression that is ONE-TO-ONE with a metric row, and
 * `device.device_id` is a column on the row itself. Everything else the
 * dashboard can FILTER on fails that test: `collection.collection_id` and the
 * tag rows are set-valued, so a session in three collections would multiply
 * its own data point across three series; `session.id` and `session.name` are
 * unique per row, so every series would have one member; `session.duration`
 * and the session timestamps are continuous, so every distinct value would be
 * its own series.
 */
const GROUPABLE_NATIVE_FIELDS: ReadonlySet<string> = new Set([
  "device.device_id",
]);

/**
 * The custom-field paths a card can be grouped by: `<entity>.custom.<name>`,
 * for the two entities that are one-to-one with a metric row.
 *
 * A session's custom fields key off its session id, and a device's off the
 * `device_id` already on the metric row, so both joins are 1:1. A COLLECTION's
 * are not: a session belongs to any number of collections, so grouping by one
 * of their custom fields would enter a session's data point into several
 * series at once — the same set-valuedness that keeps
 * `collection.collection_id` out of the native list.
 */
const GROUPABLE_CUSTOM_FIELD_PREFIXES = ["device.custom.", "session.custom."];

/** Every custom-field row is addressed as `<entity>.custom.<name>` — the same
 *  segment `buildDashboardCustomFieldFilterDefinitions` builds it from. */
const CUSTOM_FIELD_PATH = ".custom.";

/**
 * The filter types a custom field can be grouped by: the discrete ones.
 *
 * `numeric` and `date` custom fields (Number / Timestamp on the wire) are
 * continuous, so grouping by one would draw a series per distinct value.
 */
const GROUPABLE_CUSTOM_FIELD_TYPES: ReadonlySet<FilterDefinition["type"]> =
  new Set(["string", "enum", "boolean"]);

/**
 * Whether a chart card can be grouped by this filter definition.
 *
 * Kept HERE, beside the dashboard's own filter list, rather than as a
 * `groupable` flag on the shared {@link FilterDefinition}: that type is used
 * by every resource grid in the app and nothing else in the app groups, so a
 * flag there would be a platform-wide axis minted for one feature in one
 * subsystem.
 */
function isGroupable(definition: FilterDefinition): boolean {
  if (!definition.field.includes(CUSTOM_FIELD_PATH)) {
    return GROUPABLE_NATIVE_FIELDS.has(definition.field);
  }
  return (
    GROUPABLE_CUSTOM_FIELD_PREFIXES.some((prefix) =>
      definition.field.startsWith(prefix),
    ) && GROUPABLE_CUSTOM_FIELD_TYPES.has(definition.type)
  );
}

/**
 * The Group by picker's options, DERIVED from the same `FilterDefinition[]`
 * the dashboard's filter menu is built from.
 *
 * Derived rather than declared, so a custom field added to an org appears in
 * both menus at once and one place on the client decides what is groupable.
 * The definitions are passed through untouched — the picker is the same
 * `FieldPicker` the filter menu uses, and reads the same `name`, `type` and
 * `icon` off them — and in the order they arrived, which the filter menu has
 * already sorted (see `sortByFieldToken`).
 */
export function groupableFieldDefinitions(
  definitions: FilterDefinition[],
): FilterDefinition[] {
  return definitions.filter(isGroupable);
}
